import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import BrandMark from "@/components/BrandMark";

export default function AcceptContract() {
  const { t } = useTranslation();
  const { refresh, logout } = useAuth();
  const navigate = useNavigate();
  const [accepted, setAccepted] = useState(false);
  const [scrolledEnd, setScrolledEnd] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const scrollRef = useRef(null);

  const sections = t("contract.sections", { returnObjects: true }) || [];

  const onScroll = (e) => {
    const el = e.target;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 24) {
      setScrolledEnd(true);
    }
  };

  // If content fits without scrolling, mark as read after a short delay
  useEffect(() => {
    const t = setTimeout(() => {
      const el = scrollRef.current;
      if (el && el.scrollHeight <= el.clientHeight + 4) setScrolledEnd(true);
    }, 800);
    return () => clearTimeout(t);
  }, []);

  const onAccept = async () => {
    if (!scrolledEnd) { toast.error(t("contract.must_scroll")); return; }
    if (!accepted) { toast.error(t("contract.must_check")); return; }
    setSubmitting(true);
    try {
      await api.post("/auth/accept-contract");
      await refresh();
      toast.success(t("contract.accepted_toast"));
      navigate("/home");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 px-5 py-8">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <BrandMark size="md" />
          </div>
          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            <button onClick={async () => { await logout(); navigate("/login"); }}
                    className="text-xs text-zinc-500 hover:text-zinc-300"
                    data-testid="contract-cancel-logout">
              {t("common.logout")}
            </button>
          </div>
        </div>

        <div className="flex items-start gap-3 mb-6">
          <div className="w-10 h-10 rounded-md bg-[#FFD700]/15 text-[#FFD700] grid place-items-center shrink-0">
            <ShieldCheck className="w-5 h-5" />
          </div>
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-[#FFD700] mb-2">{t("contract.page_title")}</p>
            <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">{t("contract.heading")}</h1>
            <p className="text-zinc-400 mt-2 text-sm">{t("contract.page_subtitle")}</p>
          </div>
        </div>

        <Card className="bg-zinc-900/60 border-zinc-800 overflow-hidden">
          <ScrollArea className="h-[420px]" onScrollCapture={onScroll}>
            <div ref={scrollRef} className="p-6 space-y-4 text-sm leading-relaxed text-zinc-300" data-testid="contract-body">
              <p className="italic text-zinc-400">{t("contract.intro")}</p>
              {sections.map((s, i) => (
                <div key={`section-${i}-${s.title}`}>
                  <h3 className="font-semibold text-zinc-100 mb-1">{s.title}</h3>
                  <p>{s.body}</p>
                </div>
              ))}
              <div id="contract-end" className="text-xs text-zinc-500 pt-2 border-t border-zinc-800">{t("contract.doc_end")}</div>
            </div>
          </ScrollArea>
        </Card>

        {!scrolledEnd && (
          <p className="text-xs text-zinc-500 mt-3 text-center">{t("contract.scroll_hint")}</p>
        )}

        <div className="mt-6 flex items-start gap-3 bg-zinc-900/40 border border-zinc-800 rounded-lg p-4">
          <Checkbox
            id="accept"
            checked={accepted}
            onCheckedChange={(v) => setAccepted(Boolean(v))}
            disabled={!scrolledEnd}
            className="mt-1 data-[state=checked]:bg-[#FFD700] data-[state=checked]:text-black data-[state=checked]:border-[#FFD700]"
            data-testid="contract-accept-checkbox"
          />
          <label htmlFor="accept" className={`text-sm leading-snug ${scrolledEnd ? "text-zinc-200" : "text-zinc-500"} cursor-pointer`}>
            {t("contract.checkbox_label")}
          </label>
        </div>

        <Button onClick={onAccept} disabled={submitting || !accepted || !scrolledEnd}
                className="w-full mt-4 h-12 bg-[#FFD700] hover:bg-[#FACC15] text-black font-semibold disabled:opacity-40"
                data-testid="contract-submit-button">
          {submitting ? t("contract.accepting") : t("contract.accept")}
        </Button>
      </div>
    </div>
  );
}
